import React, { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { LicensePlan, accessSections, isReadOnly, isRenewalLocked } from "../../../store/actions/PlanActions";
import { ASSIGN_CATEGORY, RENEWAL_CATEGORY, renewalDescription } from "./renewal";
import { showModal, hideModal } from "../../../store/actions/modalActions";
import {
  ALL_AI_FEATURES,
  ALL_SECTIONS,
  UPGRADE_PATH,
  aiFeatureLabel,
  sectionLabel,
} from "../../../config/entitlementKeys";

/*
  THE FOURTH COPY OF THE LABEL MAP, and the last one.

  This had `clients`, no `reviews`, no `matter_economics`, no `drafting` - so
  a 403 naming a key it did not know fell through to the raw key and told the
  user their plan does not include "matter_economics". It reads the canonical
  vocabulary now, like the overlay and the gate hook.
*/
const getSectionLabels = (t: (key: string) => string): Record<string, string> =>
  Object.fromEntries(ALL_SECTIONS.map((key) => [key, t(sectionLabel(key))]));

const getAiFeatureLabels = (t: (key: string) => string): Record<string, string> =>
  Object.fromEntries(ALL_AI_FEATURES.map((key) => [key, t(aiFeatureLabel(key))]));

/* ─────────────────────────────────────────────────────────
   Find which plan/addon unlocks a given section or feature
   ───────────────────────────────────────────────────────── */
function findUnlockingPlan(
  section: string | null,
  aiFeature: string | null,
  plans: LicensePlan[],
  addons: LicensePlan[]
): { plan: LicensePlan | null; isAddon: boolean } {
  for (const addon of addons) {
    if (
      (section && addon.feature_gates?.sections?.includes(section)) ||
      (aiFeature && addon.feature_gates?.ai_features?.includes(aiFeature))
    ) {
      return { plan: addon, isAddon: true };
    }
  }
  for (const plan of plans) {
    if (
      (section && plan.feature_gates?.sections?.includes(section)) ||
      (aiFeature && plan.feature_gates?.ai_features?.includes(aiFeature))
    ) {
      return { plan, isAddon: false };
    }
  }
  return { plan: null, isAddon: false };
}

/* ─────────────────────────────────────────────────────────
   LicenseErrorHandler - global listener for 403 licensing errors
   from the axios interceptor. Shows the upgrade modal.
   
   Mount once in App.tsx or your top-level layout.
   ───────────────────────────────────────────────────────── */
export default function LicenseErrorHandler() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const plans = useSelector((state: RootState) => state.license?.plans ?? []);
  const addons = useSelector((state: RootState) => state.license?.addons ?? []);
  /* Read from the store as it stands: false while unloaded, which is fine -
     a LICENSE_EXPIRED / LICENSE_READ_ONLY code says it on its own. Grace is
     NOT renewal-locked: a grace org holds its plan, so a SECTION 403 there is
     a real plan gap and is worded as one. */
  const isExpired = useSelector((state: RootState) => isRenewalLocked(state.license?.access));
  /* Read-only AND still able to read: the §4.3 read_only keeps the last
     licence's sections for GET. A legacy `expired` org with zero sections is
     read-only in name only - it cannot view either - so it keeps the
     "expired" wording. */
  const orgReadOnly = useSelector(
    (state: RootState) =>
      isReadOnly(state.license?.access) && accessSections(state.license?.access).length > 0
  );
  const isOrgAdmin = useSelector(
    (state: RootState) => state.user?.userInfo?.is_org_admin === true
  );

  /* Is a license/upgrade modal the one currently open? Tracked via a ref so the
     route-change cleanup below fires on navigation only - not every time the
     modal's open state flips. */
  const licenseModalOpen = useSelector(
    (state: RootState) =>
      state.modal?.showModal === true && state.modal?.type === "license-request"
  );
  const licenseModalOpenRef = useRef(licenseModalOpen);
  useEffect(() => {
    licenseModalOpenRef.current = licenseModalOpen;
  }, [licenseModalOpen]);

  /*
    CLEAR A STALE UPGRADE MODAL ON NAVIGATION.

    The global modal is not part of the route, so nothing dismissed it when the
    user moved on - a modal raised by a 403 on one page persisted, and stacked,
    across every page after it. On any path change, if the open modal is the
    license one, hide it. The overlay owns the per-page lock UX; a leftover
    global modal never should.
  */
  useEffect(() => {
    if (licenseModalOpenRef.current) {
      dispatch(hideModal());
    }
  }, [location.pathname, dispatch]);

  const handleLicenseError = useCallback(
    (event: Event) => {
      // Skip if the LicenseOverlay is already showing on this page
      if ((window as any).__ponsLicenseOverlayActive) return;

      /*
        A LICENCE 403 IS ALWAYS SHOWN - loaded or not, enforced client-side or
        not. The server is the authority: if it refused, the user is told why.
        This used to drop the 403 while entitlements were unloaded, which on a
        host that never loaded them (EU) meant every refusal was silent. If the
        page locks once entitlements land, `LicenseOverlay` closes this modal
        so there is still only one lock on screen.
      */

      const detail = (event as CustomEvent).detail;
      if (!detail?.code) return;

      const { code, required_section, required_feature, limit_key, current_count, limit_value } = detail;

      const sectionLabels = getSectionLabels(t);
      const aiFeatureLabels = getAiFeatureLabels(t);

      let title = t("Upgrade required");
      let description = detail.message || "";
      let category: string | undefined;
      let inputData: string | undefined;

      /* A read-only (or legacy expired) org meets SECTION_NOT_LICENSED /
         AI_FEATURE_NOT_LICENSED too. The reason is the ended licence, not the
         plan, so that is what it is told. */
      const expiredHere =
        code === "LICENSE_EXPIRED" ||
        code === "LICENSE_READ_ONLY" ||
        (isExpired && (code === "SECTION_NOT_LICENSED" || code === "AI_FEATURE_NOT_LICENSED"));

      if (expiredHere) {
        const featureLabel =
          code === "SECTION_NOT_LICENSED"
            ? sectionLabels[required_section] || required_section
            : code === "AI_FEATURE_NOT_LICENSED"
            ? aiFeatureLabels[required_feature] || required_feature
            : undefined;
        /* Read-only wording for the code itself, and for any licence 403
           met while the org is read-only. Only the legacy LICENSE_EXPIRED
           (and unknown states flagged needs_renewal) still say "expired". */
        const readOnly =
          code === "LICENSE_READ_ONLY" || (orgReadOnly && code !== "LICENSE_EXPIRED");
        title = readOnly ? t("Read-only mode") : t("License expired");
        description = renewalDescription({
          isOrgAdmin,
          feature: featureLabel || undefined,
          readOnly,
        });
        category = RENEWAL_CATEGORY;
      } else {
        switch (code) {
          case "SECTION_NOT_LICENSED": {
            const sectionLabel = sectionLabels[required_section] || required_section || t("this feature");
            const { plan, isAddon } = findUnlockingPlan(required_section, null, plans, addons);
            description = `Your current plan does not include access to <strong>${sectionLabel}</strong>. ${
              isAddon && plan
                ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this feature.`
                : plan
                ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this feature.`
                : t("Please upgrade your plan to access this feature.")
            }`;
            title = sectionLabel;
            category = isAddon ? "addon" : "base";
            inputData = required_section || undefined;
            break;
          }
          case "AI_FEATURE_NOT_LICENSED": {
            const featureLabel = aiFeatureLabels[required_feature] || required_feature || t("this AI feature");
            const { plan, isAddon } = findUnlockingPlan(null, required_feature, plans, addons);
            description = `Your current plan does not include <strong>${featureLabel}</strong>. ${
              isAddon && plan
                ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this capability.`
                : plan
                ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this capability.`
                : t("Please upgrade your plan to access this capability.")
            }`;
            title = featureLabel;
            category = isAddon ? "addon" : "base";
            inputData = required_feature || undefined;
            break;
          }
          case "ADDON_NOT_ASSIGNED": {
            /* The org bought this add-on; this user has no seat on it. Never a
               buy button - buying it again would only meet ADDON_ALREADY_ACTIVE.
               An admin assigns seats on the plans page; a member asks. */
            const key = required_section || required_feature;
            const featureLabel =
              (required_section && sectionLabels[required_section]) ||
              (required_feature && aiFeatureLabels[required_feature]) ||
              key ||
              t("this feature");
            title = t("Add-on not assigned");
            description = isOrgAdmin
              ? t("Your organization has {{feature}}, but you have not been assigned a seat on it. Assign yourself a seat on the Plans page.", { feature: featureLabel })
              : t("Your organization has {{feature}}, but you have not been assigned a seat on it. Ask your administrator to assign it to you.", { feature: featureLabel });
            category = ASSIGN_CATEGORY;
            inputData = key || undefined;
            break;
          }
          case "ADDON_NOT_ELIGIBLE": {
            title = t("Add-on not available");
            description = t(
              "Add-ons are only available on the PONS Starter plan. Upgrade to PONS Starter to add individual features, or to PONS Full for everything."
            );
            if (!isOrgAdmin) description += ` ${t("Ask your administrator to change your organization's plan.")}`;
            category = "base";
            break;
          }
          case "USAGE_LIMIT_REACHED": {
            const limitLabel = (limit_key || "").replace("max_", "").replace(/_/g, " ");
            title = t("Usage limit reached");
            description = `You've used <strong>${current_count || "all"}</strong> of your <strong>${limit_value || ""}</strong> ${limitLabel}. Upgrade your plan to increase this limit.`;
            if (!isOrgAdmin) description += ` ${t("Ask your administrator to change your organization's plan.")}`;
            break;
          }
          case "NO_BASE_SEAT": {
            /* A member cannot fix this on /plans - only an admin assigns
               seats. So a member gets the same request NoSeatGate sends
               (`base_plan_seat`), and only an admin is sent to the plans page. */
            title = t("No active seat");
            description = `${t("You don't currently have a seat on your organization's plan.")} ${
              isOrgAdmin
                ? t("Go to Plans & Pricing to manage seat assignments.")
                : t("Contact your administrator to get a seat assigned.")
            }`;
            category = "base";
            inputData = isOrgAdmin ? undefined : "base_plan_seat";
            break;
          }
        }
      }

      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: title,
          subMsg: description,
          title,
          category,
          inputData,
          /* Only an admin is ever shown the button that calls this (see
             LicenseGateModal) - members are not sent to /plans. */
          onConfirm: () => {
            if (isOrgAdmin) history.push(UPGRADE_PATH);
          },
        })
      );
    },
    [dispatch, history, plans, addons, t, isExpired, orgReadOnly, isOrgAdmin]
  );

  useEffect(() => {
    window.addEventListener("license-error", handleLicenseError);
    return () => {
      window.removeEventListener("license-error", handleLicenseError);
    };
  }, [handleLicenseError]);

  return null; // This component renders nothing - it's just an event listener
}
